import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { IUserList, User } from '../models/user';
import { BaseRestService } from './base-rest.service';
import { UserClaim } from '../models/user-claim';
import { environment } from 'environments/environment';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class UserService extends BaseRestService<User, IUserList> {
  private currentUserId: number | null = -1;
  private currentUserUid: string | null = '';
  private currentUser: User | null;
  private currentUserClaims: UserClaim[] | null = [];

  _selectedUsers: number[] = [];
  public userAggregate: any;

  filteringByUser = new BehaviorSubject<boolean>(false);
  filteringByUserChanged$ = this.filteringByUser.asObservable();
  onSelectedUsersChanged: ReplaySubject<number[]> = new ReplaySubject<number[]>(
    1
  );
  onCurrentUserChanged: ReplaySubject<User> = new ReplaySubject<User>(
    undefined
  );

  constructor(http: HttpClient, protected router: Router) {
    super(UserService, http, 'api/users', 'user', router);
    // enable caching
    this.cacheEnabled = true;
    this.cacheTimeout = 100;

    this.onChanged.subscribe((s) => {
      const user = this.getCache().find((i) => i.id == this.currentUserId);
      if (user) {
        this.currentUser = user;
      }
    });
  }

  get selectedUsers() {
    return this._selectedUsers;
  }

  resetCurrentUser() {
    this.currentUserId = -1;
    this.currentUserUid = null;
    this.currentUserClaims = null;
    this.currentUser = null;
    this.onCurrentUserChanged.next(undefined);
  }

  setCurrentUserObject(currentUser: User, currentUserClaims: UserClaim[]) {
    this.currentUserId = currentUser.id || null;
    this.currentUserUid = currentUser.uid || null;
    this.currentUserClaims = currentUserClaims;
    this.currentUser = currentUser || null;
    this.onCurrentUserChanged.next(this.currentUser);
  }

  public getCurrentUserId(): number | null {
    return this.currentUserId;
  }

  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  getClaim(claimName: string): string | null {
    const claims = this.getCurrentUserClaims();
    if (claims == null) {
      return '';
    }
    const subClaim: UserClaim | undefined = claims.find((c) => {
      return c.type === claimName;
    });
    if (subClaim != null) {
      return subClaim.value;
    }
    return null;
  }

  public getCurrentUserUid(): string | null {
    if (this.currentUserUid) {
      return this.currentUserUid;
    }
    const subClaim = this.getClaim('sub');
    if (subClaim) {
      this.currentUserUid = subClaim;
    }
    return this.currentUserUid;
  }

  public getCurrentUserClaims(): UserClaim[] | null {
    return this.currentUserClaims;
  }

  public getCurrentUserCustomerName(): string {
    return this.getClaim('Customer');
  }
  public getCurrentUserPrivs(): string {
    var privs = this.getClaim('INCASE365.AAB-Privs');
    if (!privs) return '';
    return privs.trim().toLowerCase() || 'user';
  }

  public hasPriv(requiredPrivs: string): boolean {
    return AuthService.hasPrivInternal(
      this.getCurrentUserPrivs(),
      requiredPrivs
    );
  }

  getActiveUsersForOrganisation(
    organisationId?: number,
    IncludeAdmin: boolean = true
  ): Observable<User[]> {
    var currentUserId = this.getCurrentUserId();
    if (environment.limitAccessToSubjectsByOrg === true) {
      return this.getAllData().pipe(
        take(1),
        map((users) => {
          let activeUsers = users.filter((user) => user.canLogin === true);

          // check if the current user (if specified) is included in the activeUsers...
          if (
            currentUserId &&
            currentUserId > -1 &&
            !activeUsers.find((x) => x.id === currentUserId)
          ) {
            // ...not already in the list, filter from our list of users and add
            const currentUser = users.find((user) => user.id === currentUserId);
            if (currentUser) {
              activeUsers.push(currentUser);
            }
          }

          // Filter on organisation if 'LimitAccessToSubjectsByOrg' is true
          activeUsers = activeUsers.filter(
            (i) =>
              (i.organisationId || -1) == organisationId ||
              (i.role.toLowerCase() === 'multiorgadmin' &&
                i.userOrganisations.findIndex(
                  (j) => j.organisationId === organisationId
                ) > -1) ||
              ((i.isAdmin || false) === true && IncludeAdmin == true)
          );

          if (Array.isArray(activeUsers) && activeUsers.length > 0) {
            activeUsers = activeUsers.map((user) => new User(user));
            // tslint:disable-next-line:only-arrow-functions
            activeUsers.sort(function (a, b) {
              return a.fullName.localeCompare(b.fullName);
            });
          }

          return activeUsers;
        })
      );
    } else {
      return this.getActiveUsers(currentUserId);
    }
  }

  // MRD - most of the time we will be using this function to populate a list for selecting a user
  // For convenience I have therefore modified it to return a sorted list of active users by fullName
  getActiveUsers(currentUserId?: number | string | null): Observable<User[]> {
    return this.getAllData().pipe(
      map((users) => {
        let activeUsers = users.filter((user) => user.canLogin === true);

        // check if the current user (if specified) is included in the activeUsers...
        if (
          currentUserId &&
          !activeUsers.find(
            (x) => x.id === currentUserId || x.uid === currentUserId
          )
        ) {
          // ...not already in the list, filter from our list of users and add
          const currentUser = users.find(
            (user) => user.id === currentUserId || user.uid === currentUserId
          );
          if (currentUser) {
            activeUsers.push(currentUser);
          }
        }

        // Filter on organisation if 'LimitAccessToSubjectsByOrg' is true
        if (environment.limitAccessToSubjectsByOrg === true) {
          let loggedInUser = users.find(
            (user) => user.id === this.getCurrentUserId()
          );
          if (
            loggedInUser &&
            loggedInUser.organisationId &&
            loggedInUser.organisationId > 0
          ) {
            activeUsers = activeUsers.filter(
              (i) =>
                i.organisationId === loggedInUser.organisationId ||
                (i.role.toLowerCase() === 'multiorgadmin' &&
                  i.userOrganisations.findIndex(
                    (j) => j.organisationId === loggedInUser.organisationId
                  ) > -1)
            );
          }
        }

        if (Array.isArray(activeUsers) && activeUsers.length > 0) {
          activeUsers = activeUsers.map((user) => new User(user));
          // tslint:disable-next-line:only-arrow-functions
          activeUsers.sort(function (a, b) {
            return a.fullName.localeCompare(b.fullName);
          });
        }

        return activeUsers;
      })
    );
  }

  getAllActiveUsersList(): User[] {
    return this.getCache()
      .filter((user) => {
        return user.canLogin === true;
      })
      .map((user) => {
        return new User(user);
      });
  }

  updateUser(
    managerId?: number,
    costPerHour?: number,
    userUid?: string,
    receiveNewReferralNotifications?: boolean,
    receiveRetentionNotifications?: boolean,
    canViewArchive?: boolean,
    hasCasePrepAccess?: boolean,
    organisationId?: number
  ) {
    if (userUid) {
      this.get(userUid).subscribe((user: User) => {
        if (user && user.uid) {
          user.managerId = managerId;
          user.costPerHour = costPerHour;
          user.receiveNewReferralNotifications =
            receiveNewReferralNotifications;
          user.receiveRetentionNotifications = receiveRetentionNotifications;
          user.canViewArchive = canViewArchive;
          user.hasCasePrepAccess = hasCasePrepAccess;
          user.organisationId = organisationId;
          this.save(user);
        }
      });
    }
  }

  getUserById(userId: number): User | undefined {
    return this.getCache().find((user) => user.id === userId);
  }

  hydrate(item: User): Observable<User> {
    return of(new User(item));
  }

  // fire onSelectedUsersChanged to broadcast array of user (ids) to subscribers
  selectedUsersChanged(users: number[]) {
    // console.log('Raise selected users changed', users);
    this._selectedUsers = users;
    this.onSelectedUsersChanged.next(users);
  }
}
